import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { Close } from "@mui/icons-material";
import { SparkLineChart } from "@mui/x-charts";
import { DateTime } from "luxon";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { SinglePostFeed } from "#next/components/post/SinglePost";
import { Carousel } from "#next/components/reusable/Carousel";
import { LanguageTable } from "#next/components/preview/visuals/LanguageTable";
import { RankedBarChart } from "#next/components/preview/visuals/RankedBarChart";
import { AggregationBucket } from "#next/components/catalog/base/shared";
import { TPreviewResult } from "#next/components/search-builder/shared";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "space-between",
}));

const average = (arr: number[]) =>
  arr.reduce((acc, v, i, a) => acc + v / a.length, 0);

export const roundToTwoDecimalPlaces = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

const calcChange = (x: number, y: number) =>
  roundToTwoDecimalPlaces(((x - y) / y) * 100);

const getPercentChange = (aggregations: AggregationBucket[]) => {
  const oneWeekAgo = DateTime.now().minus({ days: 7 });
  const lastWeeksAverage = average(
    aggregations
      .filter((agg) => DateTime.fromMillis(Number(agg.key)) >= oneWeekAgo)
      .map((agg) => agg.doc_count),
  );
  const previousThreeWeeksAverage = average(
    aggregations
      .filter((agg) => DateTime.fromMillis(Number(agg.key)) < oneWeekAgo)
      .map((agg) => agg.doc_count),
  );
  return calcChange(lastWeeksAverage, previousThreeWeeksAverage);
};

const TrendIcon = ({ percentage }: { percentage: number }) => {
  if (percentage > 0) return <TrendingUpIcon sx={{ color: "green" }} />;
  if (percentage < 0) return <TrendingDownIcon sx={{ color: "red" }} />;
  return <TrendingFlatIcon />;
};

const formatPercentageChangeString = (percentage: number) => {
  if (percentage > 0) return `an increase of ${Math.abs(percentage)}%`;
  if (percentage < 0) return `a decrease of ${Math.abs(percentage)}%`;
  return `no change`;
};

export const RightSideDrawerContent = ({
  data,
  handleDrawerClose,
  searchName,
}: {
  data: TPreviewResult;
  handleDrawerClose: () => void;
  searchName?: string;
}) => {
  const percentageChange =
    data.resultsPerDay && data.resultsPerDay.buckets
      ? getPercentChange(data.resultsPerDay.buckets)
      : 0; // should be null if there are no results per day?
  const percentageChangeString = formatPercentageChangeString(percentageChange);
  const header = searchName ? `Preview: "${searchName}"` : `Preview`;

  return (
    <>
      <DrawerHeader sx={{ display: "block" }}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <IconButton size="small" onClick={handleDrawerClose}>
            <Close />
          </IconButton>
        </Box>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography variant="h3">{header}</Typography>
        </Box>
      </DrawerHeader>
      <List>
        <ListItem sx={{ flexDirection: "column" }}>
          {data.resultsPerDay && (
            <Box sx={{ width: "80%" }}>
              <SparkLineChart
                data={data.resultsPerDay.buckets.map(
                  (bucket) => bucket.doc_count,
                )}
                height={100}
              />
            </Box>
          )}
          <ListItemText>
            {`${data.totalCount.toLocaleString()} results in the last 30
            days`}
          </ListItemText>
        </ListItem>
        {data.totalCount > 0 && (
          <>
            <ListItem>
              <ListItemIcon>
                <TrendIcon percentage={percentageChange} />
              </ListItemIcon>
              <ListItemText>
                {`In the last week, there was ${percentageChangeString} in
            related activity compared to the previous three weeks.`}
              </ListItemText>
            </ListItem>
            <ListItem>
              <Carousel
                steps={[
                  {
                    label: "Scores",
                    component: (
                      <LanguageTable
                        negativeBoxPlot={data.negativeBoxPlot}
                        offensiveBoxPlot={data.offensiveBoxPlot}
                        hatefulBoxPlot={data.hatefulBoxPlot}
                        violentBoxPlot={data.violentBoxPlot}
                      />
                    ),
                  },
                  {
                    label: "Platforms",
                    component: (
                      <RankedBarChart
                        aggregation={data.platformCounts}
                        label="Platform"
                      />
                    ),
                  },
                  {
                    label: "Users",
                    component: (
                      <RankedBarChart
                        aggregation={data.userCounts}
                        label="User"
                      />
                    ),
                  },
                  {
                    label: "Languages",
                    component: (
                      <RankedBarChart
                        aggregation={data.languageCounts}
                        label="Language"
                      />
                    ),
                  },
                ]}
              />
            </ListItem>
            <ListItem sx={{ display: "block" }}>
              <ListItemText>Top Results</ListItemText>
              <SinglePostFeed posts={data.posts} trackActionPrefix="Preview" />
            </ListItem>
          </>
        )}
      </List>
    </>
  );
};
